import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Avatar, Chip, Button, CircularProgress,
  Alert, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import axios from 'axios';
import { ProjectConfig } from 'Global';

export default function PatientQuotesView() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<any>(null);
  const [confirmQuote, setConfirmQuote] = useState<any>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${ProjectConfig.api_url}/quotes/patient-quotes`, { params: { patient_token: token } })
      .then(r => {
        if (r.data.status === 'success') setData(r.data);
        else setError(r.data.message || 'Error al cargar las cotizaciones');
      })
      .catch(() => setError('Error de conexión'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!confirmQuote) return;
    setAccepting(true);
    try {
      const resp = await axios.post(`${ProjectConfig.api_url}/quotes/accept`, {
        quote_id: confirmQuote.quote_id,
        patient_token: token,
      });
      if (resp.data.status === 'success') {
        setAccepted(true);
        setConfirmQuote(null);
        setData((prev: any) => ({
          ...prev,
          request: { ...prev.request, status: 'accepted' },
          quotes: prev.quotes.map((q: any) =>
            q.quote_id === confirmQuote.quote_id ? { ...q, status: 'accepted' } : q
          ),
        }));
      }
    } catch {
      setError('Error al aceptar la cotización');
    } finally {
      setAccepting(false);
    }
  };

  const formatPrice = (p: any) => {
    if (!p || p === '0') return 'A convenir';
    return `$${Number(p).toLocaleString('es-CL')}`;
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 6, px: 2 }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f5fb', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" fontWeight={800} color="#125095">Especialista en Heridas</Typography>
          <Typography color="text.secondary" fontSize={14}>Tus cotizaciones de enfermeras</Typography>
        </Box>

        {/* Patient request summary */}
        {data?.request && (
          <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid #dde6f5' }}>
            <Typography fontWeight={700} fontSize={15} color="#125095" gutterBottom>Tu solicitud</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" sx={{ color: '#888' }} />
                <Typography fontSize={14}>{data.request.nombre} {data.request.apellido}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOnIcon fontSize="small" sx={{ color: '#888' }} />
                <Typography fontSize={14}>{data.request.comuna_name}</Typography>
              </Box>
              <Chip
                icon={<MedicalServicesIcon fontSize="small" />}
                label={data.request.procedimiento?.replace(/_/g, ' ')}
                size="small"
                sx={{ bgcolor: '#e4eef9', color: '#125095' }}
              />
              <Chip
                label={data.request.status === 'accepted' ? 'Cotización aceptada' : data.quotes?.length > 0 ? `${data.quotes.length} cotización(es)` : 'Esperando cotizaciones'}
                size="small"
                color={data.request.status === 'accepted' ? 'success' : 'default'}
              />
            </Box>
          </Paper>
        )}

        {accepted && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            <strong>¡Cotización aceptada!</strong> El administrador coordinará el contacto con la enfermera en breve.
          </Alert>
        )}

        {/* Quotes list */}
        {data?.quotes?.length === 0 ? (
          <Paper elevation={0} sx={{ borderRadius: 3, p: 5, border: '1px solid #dde6f5', textAlign: 'center' }}>
            <Typography color="text.secondary" fontSize={15}>
              Aún no hay cotizaciones disponibles. Las enfermeras de tu comuna están revisando tu solicitud.
              <br />Te notificaremos por correo cuando haya respuestas.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography fontWeight={700} fontSize={15} color="text.secondary">
              {data.quotes.length} enfermera(s) cotizaron tu solicitud
            </Typography>
            {data.quotes.map((quote: any) => (
              <Paper
                key={quote.quote_id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  border: quote.status === 'accepted' ? '2px solid #2e7d32' : '1px solid #dde6f5',
                  bgcolor: quote.status === 'accepted' ? '#f1faf2' : '#fff',
                }}
              >
                {/* Nurse header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={quote.nurse_image || undefined}
                    sx={{ bgcolor: '#125095', width: 48, height: 48 }}
                  >
                    {quote.nurse_name?.[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700}>{quote.nurse_name}</Typography>
                    <Typography fontSize={13} color="text.secondary">Enfermera especialista</Typography>
                  </Box>
                  {quote.status === 'accepted' ? (
                    <Chip label="Cotización aceptada" color="success" icon={<CheckCircleIcon />} />
                  ) : (
                    <Typography fontWeight={800} fontSize={20} color="#125095">
                      {formatPrice(quote.precio)}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Typography fontSize={14} color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                  {quote.quote_text}
                </Typography>

                {data.request?.status !== 'accepted' && (
                  <Button
                    variant="contained"
                    onClick={() => setConfirmQuote(quote)}
                    sx={{
                      bgcolor: '#125095', borderRadius: 2, fontWeight: 700,
                      '&:hover': { bgcolor: '#0e3d78' },
                    }}
                  >
                    Acepto esta cotización
                  </Button>
                )}
              </Paper>
            ))}
          </Box>
        )}

        {/* Confirm dialog */}
        <Dialog open={!!confirmQuote} onClose={() => !accepting && setConfirmQuote(null)} maxWidth="xs" fullWidth>
          <DialogTitle>¿Confirmar cotización?</DialogTitle>
          <DialogContent>
            <Typography>
              Vas a aceptar la cotización de <strong>{confirmQuote?.nurse_name}</strong> por{' '}
              <strong>{formatPrice(confirmQuote?.precio)}</strong>.
            </Typography>
            <Typography color="text.secondary" fontSize={13} sx={{ mt: 1 }}>
              El administrador te contactará para coordinar el servicio.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmQuote(null)} disabled={accepting}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={accepting}
              sx={{ bgcolor: '#125095', '&:hover': { bgcolor: '#0e3d78' } }}
            >
              {accepting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
